import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Plus, Search, Film } from 'lucide-react'

export default function Series() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['series', page, search],
    queryFn: () => api.getSeries({ page, per_page: 20, search }),
  })

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">
          Series
        </h1>
        <button className="btn-primary shrink-0">
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Series</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search series..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-12"
        />
      </div>

      {/* Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[2/3] bg-muted rounded-lg mb-4"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          ))
        ) : data?.series?.length ? (
          data.series.map((series: any) => (
            <div key={series.id} className="group cursor-pointer">
              <div className="relative aspect-[2/3] bg-muted rounded-lg overflow-hidden mb-4">
                {series.cover_url ? (
                  <img
                    src={series.cover_url}
                    alt={series.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Film className="w-12 h-12 text-muted-foreground" />
                  </div>
                )}
                {series.rating && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-foreground/80 rounded text-background text-sm font-medium">
                    {series.rating}
                  </div>
                )}
              </div>
              <h3 className="text-base font-medium text-foreground group-hover:text-primary transition-colors">
                {series.name}
              </h3>
              <p className="text-sm text-muted-foreground">
                {series.episode_count || 0} episodes
              </p>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-base text-muted-foreground">
            No series found
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.total_pages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary"
          >
            Previous
          </button>
          <span className="text-base text-muted-foreground">
            Page {page} of {data?.total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data?.total_pages, p + 1))}
            disabled={page === data?.total_pages}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
